import { useAtom } from "jotai";
import { cloneDeep } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { segmentStateAtom } from "../../moment/types/store";
import { SupabaseSegmentService } from "../server/supabase-segment-service";

type SegmentSelectorData = { selectSegment: (id?: UUID) => Promise<void> } & (
  | {
      loading: true;
      data: null;
    }
  | {
      loading: false;
      data: {
        selectedSegment: MomentFormMoment["segment"];
        isNew: boolean;
      };
    }
);

export function useSegmentSelector(onError: (error: string) => void): SegmentSelectorData {
  const [segmentState, setSegmentState] = useAtom(segmentStateAtom);
  const [loading, setLoading] = useState(false);

  const { getValues, reset, control } = useFormContext<MomentFormMoment>();

  const selectSegment = useSupabaseCallback(
    // Load segment data when changing the selected segment
    // then cache it, if the segment was already in the cache then use it.
    // If no id provided, then reset the segment count and form values
    async ({ supabase }, id?: UUID) => {
      if (!segmentState) throw new Error("Segment state not loaded");

      setLoading(true);

      if (!id) {
        reset({ ...getValues(), segment: null });
        return setLoading(false);
      }

      if (Object.keys(segmentState.cache).includes(id)) {
        reset({ ...getValues(), segment: segmentState.cache[id]! });
        return setLoading(false);
      }

      const { data: selectedSegmentData, error: selectedSegmentError } =
        await new SupabaseSegmentService(supabase).getSegmentData(id);
      if (!selectedSegmentData || selectedSegmentError) {
        onError(selectedSegmentError.message || "Problem retrieving segments");
        return setLoading(false);
      }

      setSegmentState({
        ...segmentState,
        cache: { ...segmentState.cache, [selectedSegmentData.id]: selectedSegmentData },
      });

      reset({ ...getValues(), segment: selectedSegmentData }); //TODO: just setvalue here?

      setLoading(false);
    },
    [getValues, reset, segmentState, setSegmentState, onError],
  );
  // This section tracks when the above function is called and extracts the newly
  // dispatched segment id and segment state from the form context reset action
  useWatch({ control, name: "segment" });
  const selectedSegment = getValues("segment");

  useEffect(() => {
    if (!selectedSegment?.id || !segmentState) return;

    // if the selected segment is not in the cache, set it
    if (!segmentState.cache[selectedSegment.id]) {
      setSegmentState(
        !segmentState
          ? segmentState
          : {
              ...segmentState,
              cache: { ...segmentState.cache, [selectedSegment.id]: cloneDeep(selectedSegment) },
            },
      );
    }
  }, [selectedSegment, segmentState, setSegmentState]);

  const isNew = useMemo(
    () => !!(selectedSegment?.id && selectedSegment?.unsaved),
    [selectedSegment?.id, selectedSegment?.unsaved],
  );

  return loading
    ? { selectSegment, loading: true, data: null }
    : { selectSegment, loading: false, data: { selectedSegment, isNew } };
}
